import { atom } from 'jotai';
import { sectionDefs, jsonSectionKeys, isBlankRow } from './sectionDefs';

// The whole in-progress report lives in atoms (not component state) so tab
// switches / job reloads don't lose unsaved work. null = list view.
export const fieldReportAtom = atom(null);
export const fieldReportStepAtom = atom(0);
export const fieldReportSavingAtom = atom(false);
export const fieldReportDirtyAtom = atom(false);

// 'wizard' (guided steps) or 'sheet' (Excel-style single page). Sticky per device.
const storedView = (() => {
    try { return localStorage.getItem('fieldReportView'); } catch (_) { return null; }
})();
export const fieldReportViewAtom = atom(storedView === 'sheet' ? 'sheet' : 'wizard');

const emptySections = () =>
    Object.fromEntries(sectionDefs.map((s) => [s.key, []]));

// Every empty section opens with one blank starter row (a crew + a sub for
// labor) so the field doesn't have to hit "+ Add" for the first entry.
// Untouched starters are blank rows, which buildSavePayload filters out, so
// they never persist.
export const seedStarterRows = (sections) => {
    const out = { ...sections };
    for (const def of sectionDefs) {
        if ((out[def.key] || []).length > 0) continue;
        if (def.key === 'labor') {
            out[def.key] = [
                { group: 'crew', name: '', discipline: '', headcount: '', hoursWorked: '' },
                { group: 'sub', name: '', discipline: '', headcount: '', hoursWorked: '' },
            ];
        } else if (def.key === 'equipment' || def.hidden) {
            // No starter rows — the equipment picker adds one row per unit,
            // and hidden sections (timeCard, simple, derived storage…) have
            // their own editors that don't want a junk blank row at index 0.
            out[def.key] = [];
        } else {
            out[def.key] = [{}];
        }
    }
    return out;
};

export const newReport = ({ buId, preparedBy }) => ({
    reportId: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    buId,
    reportNumber: null,
    reportDate: new Date().toISOString().slice(0, 10),
    status: 'Draft',
    superintendent: '',
    preparedBy: preparedBy || '',
    weatherAM: '',
    weatherPM: '',
    wind: '',
    groundConditions: '',
    safetyIncidents: '',
    nearMisses: '',
    notes: '',
    sections: seedStarterRows(emptySections()),
    photos: [],
});

const numOrNull = (v) => {
    if (v === '' || v == null) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};
const strOrNull = (v) => (v === '' || v == null ? null : String(v));
const boolOrNull = (v) => (v === '' || v == null ? null : !!v);

// Labor rows ({ group, name, discipline, headcount, hoursWorked }) map to the
// typed PC.Field_Report_Labor shape: one row per crew/sub with its hours
// worked. Classification carries the group ('Crew' / 'Sub') so hydrate can
// regroup; the discipline rides in the Activity column.
const flattenLaborMatrix = (rows) => (rows || [])
    .filter((r) => !isBlankRow(r))
    .map((r, i) => ({
        rowIndex: i,
        company: strOrNull(r.group === 'sub' ? r.name : 'Strike'),
        crewForeman: strOrNull(r.name),
        classification: r.group === 'sub' ? 'Sub' : 'Crew',
        headcount: numOrNull(r.headcount),
        stHours: numOrNull(r.hoursWorked),
        costCode: null,
        otHours: null,
        activity: strOrNull(r.discipline),
        notes: strOrNull(r.notes),
    }));

// Quick Report time card rows ({ name, hours: {costCode: hrs} }) flatten to
// one typed labor row per person × phase with the CostCode set. They coexist
// with the LaborMatrix rows in PC.Field_Report_Labor; hydrate splits them back
// apart by CostCode (the timeCard JSON section stays the editing copy).
const flattenTimeCard = (rows, startIndex) => {
    const out = [];
    (rows || []).filter((r) => !isBlankRow(r)).forEach((r) => {
        Object.entries(r.hours || {}).forEach(([code, hrs]) => {
            const h = numOrNull(hrs);
            if (!h) return;
            out.push({
                rowIndex: startIndex + out.length,
                company: 'Strike',
                crewForeman: strOrNull(r.name),
                classification: 'Crew',
                headcount: 1,
                stHours: h,
                costCode: strOrNull(code),
                otHours: null,
                activity: null,
                notes: null,
            });
        });
    });
    return out;
};

// Map wizard state → api/saveFieldReport payload. Typed sections (labor,
// quantities) become their own arrays; the rest become sectionRows JSON.
export const buildSavePayload = (report, updatedBy, status) => ({
    report: {
        reportId: report.reportId,
        buId: report.buId,
        reportDate: report.reportDate,
        status: status || report.status || 'Draft',
        superintendent: strOrNull(report.superintendent),
        preparedBy: strOrNull(report.preparedBy),
        weatherAM: strOrNull(report.weatherAM),
        weatherPM: strOrNull(report.weatherPM),
        wind: strOrNull(report.wind),
        groundConditions: strOrNull(report.groundConditions),
        safetyIncidents: numOrNull(report.safetyIncidents),
        nearMisses: numOrNull(report.nearMisses),
        notes: strOrNull(report.notes),
    },
    labor: (() => {
        const matrix = flattenLaborMatrix(report.sections.labor || []);
        return matrix.concat(flattenTimeCard(report.sections.timeCard, matrix.length));
    })(),
    quantities: (report.sections.quantities || []).filter((r) => !isBlankRow(r)).map((r, i) => ({
        rowIndex: i,
        costCode: strOrNull(r.costCode),
        task: strOrNull(r.task),
        unit: strOrNull(r.unit),
        budgetQty: numOrNull(r.budgetQty),
        installedToday: numOrNull(r.installedToday),
        installedToDate: numOrNull(r.installedToDate),
        reworkedToday: numOrNull(r.reworkedToday),
        mhToday: numOrNull(r.mhToday),
    })),
    sectionRows: jsonSectionKeys.flatMap((key) =>
        (key === 'subs' ? subsFromLabor(report.sections.labor) : report.sections[key] || [])
            .filter((r) => !isBlankRow(r)).map((r, i) => ({
                section: key,
                rowIndex: i,
                costCode: strOrNull(r.costCode),
                row: r,
            })),
    ),
    updatedBy,
});

// The subs JSON section is derived from the LaborMatrix sub rows on every
// save — the typed labor table has no % complete / on-schedule columns, so
// those answers persist here and hydrate re-attaches them by sub name.
const subsFromLabor = (laborRows) => (laborRows || [])
    .filter((r) => r.group === 'sub' && !isBlankRow(r))
    .map((r) => ({
        subcontractor: r.name || '',
        poNumber: r.poNumber || '',
        poStart: r.poStart || '',
        poEnd: r.poEnd || '',
        workPerformed: r.notes || '',
        headcount: r.headcount ?? '',
        hours: r.hoursWorked ?? '',
        percentComplete: r.percentComplete ?? '',
        onSchedule: r.onSchedule || '',
    }));

const s = (v) => (v == null ? '' : String(v));
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');

// Map api/readFieldReport result → wizard state.
export const hydrateFromRead = (api) => {
    const h = api.report || {};
    const sections = emptySections();
    // Regroup flat labor rows into one row per crew/sub. Drafts saved by the
    // old per-phase format carry several rows per crew, so hours accumulate.
    const laborByKey = {};
    const laborOrder = [];
    (api.labor || []).forEach((r) => {
        // CostCode-bearing rows came from the Quick Report time card — the
        // timeCard JSON section carries them for editing; skipping here keeps
        // them out of the LaborMatrix (and from double-saving).
        if (s(r.CostCode)) return;
        const group = s(r.Classification) === 'Sub' ? 'sub' : 'crew';
        const name = s(r.CrewForeman) || s(r.Company);
        const key = `${group}|${name}`;
        if (!laborByKey[key]) {
            laborByKey[key] = { group, name, discipline: s(r.Activity), headcount: s(r.Headcount), notes: s(r.Notes), hoursWorked: '' };
            laborOrder.push(key);
        }
        const hrs = parseFloat(r.STHours);
        if (Number.isFinite(hrs)) {
            const prev = parseFloat(laborByKey[key].hoursWorked);
            laborByKey[key].hoursWorked = String(Math.round(((Number.isFinite(prev) ? prev : 0) + hrs) * 10) / 10);
        }
    });
    sections.labor = laborOrder.map((k) => laborByKey[k]);
    sections.quantities = (api.quantities || []).map((r) => ({
        costCode: s(r.CostCode),
        task: s(r.Task),
        unit: s(r.Unit),
        budgetQty: s(r.BudgetQty),
        installedToday: s(r.InstalledToday),
        installedToDate: s(r.InstalledToDate),
        reworkedToday: s(r.ReworkedToday),
        mhToday: s(r.MHToday),
    }));
    for (const sr of api.sectionRows || []) {
        if (sections[sr.section]) sections[sr.section].push(sr.row || {});
    }
    // % complete / on-schedule live on the derived subs JSON rows — put them
    // back onto the matching LaborMatrix sub row.
    for (const sub of sections.subs || []) {
        const row = sections.labor.find((l) => l.group === 'sub' && l.name === s(sub.subcontractor));
        if (!row) continue;
        if ((sub.percentComplete ?? '') !== '') row.percentComplete = s(sub.percentComplete);
        if (sub.onSchedule) row.onSchedule = s(sub.onSchedule);
        if (sub.poNumber) row.poNumber = s(sub.poNumber);
        if (sub.poStart) row.poStart = s(sub.poStart);
        if (sub.poEnd) row.poEnd = s(sub.poEnd);
    }
    // Drafts reopen with starter rows in any still-empty section; submitted
    // reports are read-only, so blank rows would just be noise.
    const status = h.Status || 'Draft';
    return {
        reportId: h.ReportID,
        buId: h.BU_ID,
        reportNumber: h.ReportNumber,
        reportDate: dateOnly(h.ReportDate),
        status,
        superintendent: s(h.Superintendent),
        preparedBy: s(h.PreparedBy),
        weatherAM: s(h.WeatherAM),
        weatherPM: s(h.WeatherPM),
        wind: s(h.Wind),
        groundConditions: s(h.GroundConditions),
        safetyIncidents: s(h.SafetyIncidents),
        nearMisses: s(h.NearMisses),
        notes: s(h.Notes),
        sections: status === 'Submitted' ? sections : seedStarterRows(sections),
        // photos carries sub documents (docType timeTicket/progressReport,
        // uploaded from the Lab/Eq step) and delivery tickets (deliveryTicket,
        // Material Deliveries step) too; location holds the file name for docs.
        photos: (api.photos || []).map((p) => ({
            photoId: p.PhotoID,
            sasUrl: p.sasUrl,
            docType: s(p.DocType) || 'photo',
            contentType: s(p.ContentType),
            caption: s(p.Caption),
            location: s(p.Location),
        })),
    };
};
